import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { media } from 'theme/breakpoints';
import ls from 'img/ui/ls-favicon.png';
import checkmark from 'img/ui/checkmarkGreen.svg';
import cross from 'img/ui/cross.svg';
import TableMobile from './TableMobile';

const Section = styled.section`
  padding: 7rem 0 calc(7rem + 30px);
  ${media.tablet} {
    padding: 100px 0;
  }
  & h2 {
    text-align: center;
    font-size: 28px;
    line-height: 42px;
    ${media.tablet} {
      font-size: 24px;
      line-height: 36px;
    }
  }
  & p {
    margin: 20px 0 80px;
    font-size: 18px;
    text-align: center;
    ${media.tablet} {
      margin: 20px 0 -24px;
    }
  }
`;

const FeaturesTable = styled.table`
  margin: 0;
  table-layout: fixed;
  width: 100%;
  max-width: 100%;
  ${media.tablet} {
    display: none;
  }
  & .green {
    background-color: #e8fff2;
  }
  & thead tr th {
    padding: 40px 15px;
    & img {
      max-width: 24px;
    }
  }
  & thead th {
    border: none;
    &.plan {
      font-size: 16px;
      font-weight: 800;
      text-align: center;
      vertical-align: middle;
    }
  }

  & tbody tr th {
    padding: 20px 0 20px 15px;
    font-size: 18px;
    font-weight: 500;
    position: relative;
    border-radius: 4px;
    cursor: pointer;
    span {
      display: inline-block;
      border-bottom: 1px dashed #000;
    }
  }
  & th,
  td {
    padding: 0 58px;
    text-align: center;
    vertical-align: middle !important;
    border: none;
    font-size: 16px;
    font-weight: 800;
    position: relative;
    &:last-child {
      border-radius: 4px;
    }
    & span.planned {
      display: inline-block;
      align-items: center;
      justify-content: center;
      font-style: normal;
      color: #000;
      height: 24px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 800;
      line-height: 24px;
      text-align: center;
      padding: 0 8px;
    }
  }
  .subinfo {
    color: #788487;
    font-weight: 300;
    font-size: 0.75rem;
    max-width: 265px;
  }
  & td.cross {
    background-color: #f5005708;
  }
  & tr {
    th {
      &:first-child {
        text-align: left;
      }
    }
  }

  @media (max-width: 810px) {
    width: 95%;
    text-align: center;

    & th,
    td {
      padding-left: 0;
    }
  }

  & thead {
    background-color: #fff;
  }

  & tr {
    background-color: #fff;
    border-radius: 4px;
  }

  & .icon-check {
    color: #000;
    font-size: 20px;
  }
  & .icon-cross {
    color: #f50057;
    font-size: 25px;
  }
`;

const CompareTo = ({ dataSetBase, LSbase, compareBase, compareTo, icon, title, desc }) => {
  const pages = [
    {
      name: 'LiveSession',
      base: LSbase,
      key: 0,
      icon: <img src={ls} alt="favicon" title="LiveSession Favicon" />,
    },
    {
      name: compareTo,
      base: compareBase,
      key: 1,
      icon: <img src={icon} alt="favicon" title={`${compareTo} Favicon`} />,
    },
  ];

  const renderIcon = (item) => {
    return item.includes('check') ? (
      <img src={checkmark} alt="check" />
    ) : (
      <>
        {!item.includes('planned') && <img src={cross} alt="cross" />}
        {item.includes('planned') && <span className="planned">Coming soon</span>}
      </>
    );
  };

  return (
    <Section>
      <h2>{title}</h2>
      <p>{desc}</p>
      <TableMobile pages={pages} data={dataSetBase} compare />
      <FeaturesTable className="table">
        <thead>
          <tr>
            <th scope="col" aria-label="emptycol" />
            <th scope="col" className="plan green">
              {pages[0].icon} LiveSession
            </th>
            <th scope="col" className="plan">
              {pages[1].icon} {pages[1].name}
            </th>
          </tr>
        </thead>
        <tbody>
          {dataSetBase.map((item, i) =>
            item.tooltip ? (
              <tr key={item.tooltip}>
                <th scope="row">
                  <Tippy content={item.tooltip} placement="bottom-start">
                    <span>{item.name}</span>
                  </Tippy>
                </th>
                <td className="green">{renderIcon(pages[0].base[i])}</td>
                <td className={pages[1].base[i].includes('cross') ? 'cross' : ''}>
                  {renderIcon(pages[1].base[i])}
                </td>
              </tr>
            ) : (
              <tr key={item.name}>
                <th scope="row">{item.name}</th>
                <td
                  className={[pages[0].base[i].includes('cross') ? 'cross' : '', 'green'].join(' ')}
                >
                  {renderIcon(pages[0].base[i])}
                </td>
                <td className={pages[1].base[i].includes('cross') ? 'cross' : ''}>
                  {renderIcon(pages[1].base[i])}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </FeaturesTable>
    </Section>
  );
};

export default CompareTo;

CompareTo.propTypes = {
  dataSetBase: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
    }),
  ).isRequired,
  LSbase: PropTypes.arrayOf(PropTypes.node.isRequired).isRequired,
  compareBase: PropTypes.arrayOf(PropTypes.node.isRequired).isRequired,
  compareTo: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]).isRequired,
};
